import React from 'react';
import { Tooltip } from '@mui/material';
import { count } from './CaseWorkflow/caseWorkflowVocabulary';
import {
    AttentionBucket, OPERATIONS_BUCKETS, attentionInk, bucketDefinition
} from './ExecutionStats/executionStatsVocabulary';
import { dsVar } from '../theme/tokens';

export { OPERATIONS_BUCKETS };
export type { AttentionBucket };

/** Matches the window the workflow cards show, so a chip and a card agree. */
export const ATTENTION_WINDOW = '30d' as const;

const ATTENTION_PHRASES: Record<AttentionBucket, string> = {
    failed: 'with failed runs',
    stalled: 'with stalled runs',
    pendingReview: 'with runs pending review'
};

/** "2 workflows with failed runs" */
export const describeAttention = (bucket: AttentionBucket, workflows: number): string =>
    `${count(workflows)} ${workflows === 1 ? 'workflow' : 'workflows'} ${ATTENTION_PHRASES[bucket]}`;

export interface RunAttention {
    /**
     * Workflows, not runs, per bucket. A campaign with no tagged runs reports
     * its flow's runs, so summing runs would count a shared flow once per
     * workflow that uses it.
     */
    workflows: Record<AttentionBucket, number>;
    /** Workflows whose run statistics could not be loaded. */
    unavailable: number;
    staleAfterHours: number | null;
}

interface ProjectAttentionStripProps {
    /** Null until every workflow card has reported its run statistics. */
    runAttention: RunAttention | null;
    activeFilter: AttentionBucket | null;
    onFilter: (bucket: AttentionBucket | null) => void;
}

/**
 * Workflows whose runs broke, as chips on the "Your Workflows" heading that
 * filter the catalog beneath it.
 *
 * Shown only when there is something to act on. It used to be a section of its
 * own with an all-clear line, between the outcomes and the catalog; the cards and
 * the pipeline map already mark a broken workflow, and the one thing this adds —
 * filtering the catalog to those workflows — belongs beside the catalog. Nothing
 * renders until every card has reported, so it never flashes an all-clear about
 * numbers still on their way.
 */
const ProjectAttentionStrip: React.FC<ProjectAttentionStripProps> = ({
    runAttention, activeFilter, onFilter
}) => {
    if (!runAttention) return null;

    const runChips = OPERATIONS_BUCKETS.filter((bucket) => runAttention.workflows[bucket] > 0);
    if (!runChips.length) {
        // Not an all-clear: a workflow whose statistics did not load may be failing.
        return runAttention.unavailable > 0 ? (
            <span className="text-xs text-fg-muted">
                Run statistics unavailable for {count(runAttention.unavailable)} {runAttention.unavailable === 1 ? 'workflow' : 'workflows'}
            </span>
        ) : null;
    }

    return (
        <span role="group" aria-label="Workflows with failed or stalled runs" className="inline-flex flex-wrap items-center gap-2">
            {runChips.map((bucket) => {
                const active = activeFilter === bucket;
                const definition = runAttention.staleAfterHours === null
                    ? ''
                    : bucketDefinition(bucket, runAttention.staleAfterHours);
                return (
                    <Tooltip key={bucket} title={`${definition} Last ${ATTENTION_WINDOW}. Click to show only these workflows.`} arrow placement="top" describeChild>
                        <button
                            aria-pressed={active}
                            onClick={() => onFilter(active ? null : bucket)}
                            className="rounded-full border px-2.5 py-0.5 text-xs font-semibold cursor-pointer transition-colors"
                            style={{
                                color: active ? dsVar('on-accent') : attentionInk(bucket),
                                backgroundColor: active ? attentionInk(bucket) : 'transparent',
                                borderColor: active ? attentionInk(bucket) : `${attentionInk(bucket)}66`
                            }}
                        >
                            {describeAttention(bucket, runAttention.workflows[bucket])}
                        </button>
                    </Tooltip>
                );
            })}
        </span>
    );
};

export default ProjectAttentionStrip;
